import React, { useState, useCallback } from 'react';
import { Calendar, Clock, Gift, Heart, Sun, Moon, Star } from 'lucide-react';

interface AgeResult {
  years: number;
  months: number;
  days: number;
  totalDays: number;
  totalWeeks: number;
  totalHours: number;
  totalMinutes: number;
  nextBirthday: Date;
  daysUntilBirthday: number;
  dayOfWeek: string;
  zodiacSign: string;
  zodiacEmoji: string;
  birthstone: string;
  season: string;
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ZODIAC = [
  { sign: 'Capricorn', emoji: '♑', start: [1, 1], end: [1, 19] },
  { sign: 'Aquarius', emoji: '♒', start: [1, 20], end: [2, 18] },
  { sign: 'Pisces', emoji: '♓', start: [2, 19], end: [3, 20] },
  { sign: 'Aries', emoji: '♈', start: [3, 21], end: [4, 19] },
  { sign: 'Taurus', emoji: '♉', start: [4, 20], end: [5, 20] },
  { sign: 'Gemini', emoji: '♊', start: [5, 21], end: [6, 20] },
  { sign: 'Cancer', emoji: '♋', start: [6, 21], end: [7, 22] },
  { sign: 'Leo', emoji: '♌', start: [7, 23], end: [8, 22] },
  { sign: 'Virgo', emoji: '♍', start: [8, 23], end: [9, 22] },
  { sign: 'Libra', emoji: '♎', start: [9, 23], end: [10, 22] },
  { sign: 'Scorpio', emoji: '♏', start: [10, 23], end: [11, 21] },
  { sign: 'Sagittarius', emoji: '♐', start: [11, 22], end: [12, 21] },
  { sign: 'Capricorn', emoji: '♑', start: [12, 22], end: [12, 31] },
];
const BIRTHSTONES = ['Garnet', 'Amethyst', 'Aquamarine', 'Diamond', 'Emerald', 'Pearl', 'Ruby', 'Peridot', 'Sapphire', 'Opal', 'Topaz', 'Tanzanite'];

function getZodiac(month: number, day: number) {
  for (const z of ZODIAC) {
    const [sm, sd] = z.start;
    const [em, ed] = z.end;
    if ((month === sm && day >= sd) || (month === em && day <= ed)) {
      return { sign: z.sign, emoji: z.emoji };
    }
  }
  return { sign: 'Capricorn', emoji: '♑' };
}

function getSeason(month: number): string {
  if (month >= 3 && month <= 5) return 'Spring';
  if (month >= 6 && month <= 8) return 'Summer';
  if (month >= 9 && month <= 11) return 'Autumn';
  return 'Winter';
}

const AgeCalculator: React.FC = () => {
  const [birthDate, setBirthDate] = useState('');
  const [targetDate, setTargetDate] = useState(new Date().toISOString().split('T')[0]);
  const [result, setResult] = useState<AgeResult | null>(null);
  const [error, setError] = useState('');

  const calculate = useCallback(() => {
    setError('');
    if (!birthDate) { setError('Please enter your birth date'); return; }

    const birth = new Date(birthDate + 'T00:00:00');
    const target = new Date(targetDate + 'T00:00:00');

    if (birth > target) { setError('Birth date cannot be in the future'); return; }

    let years = target.getFullYear() - birth.getFullYear();
    let months = target.getMonth() - birth.getMonth();
    let days = target.getDate() - birth.getDate();

    if (days < 0) {
      months--;
      const prevMonth = new Date(target.getFullYear(), target.getMonth(), 0);
      days += prevMonth.getDate();
    }
    if (months < 0) { years--; months += 12; }

    const diffMs = target.getTime() - birth.getTime();
    const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Next birthday
    let nextBday = new Date(target.getFullYear(), birth.getMonth(), birth.getDate());
    if (nextBday <= target) nextBday = new Date(target.getFullYear() + 1, birth.getMonth(), birth.getDate());
    const daysUntilBirthday = Math.ceil((nextBday.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));

    const zodiac = getZodiac(birth.getMonth() + 1, birth.getDate());

    setResult({
      years, months, days, totalDays,
      totalWeeks: Math.floor(totalDays / 7),
      totalHours: totalDays * 24,
      totalMinutes: totalDays * 24 * 60,
      nextBirthday: nextBday,
      daysUntilBirthday,
      dayOfWeek: DAYS[birth.getDay()],
      zodiacSign: zodiac.sign,
      zodiacEmoji: zodiac.emoji,
      birthstone: BIRTHSTONES[birth.getMonth()],
      season: getSeason(birth.getMonth() + 1),
    });
  }, [birthDate, targetDate]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-blue-100 rounded-lg"><Calendar className="w-6 h-6 text-blue-600" /></div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Age Calculator</h2>
          <p className="text-sm text-gray-500">Calculate your exact age and fun facts</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
          <input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Calculate age as of</label>
          <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900" />
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

      <button onClick={calculate} className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition mb-6">
        Calculate Age
      </button>

      {result && (
        <div className="space-y-4">
          {/* Main age display */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 text-center border border-blue-100">
            <p className="text-sm text-gray-500 mb-2">Your age is</p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <div><span className="text-4xl font-bold text-blue-600">{result.years}</span><span className="text-gray-500 ml-1">years</span></div>
              <div><span className="text-4xl font-bold text-indigo-600">{result.months}</span><span className="text-gray-500 ml-1">months</span></div>
              <div><span className="text-4xl font-bold text-purple-600">{result.days}</span><span className="text-gray-500 ml-1">days</span></div>
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total Days', value: result.totalDays.toLocaleString(), icon: <Sun className="w-4 h-4" />, color: 'text-amber-600 bg-amber-50' },
              { label: 'Total Weeks', value: result.totalWeeks.toLocaleString(), icon: <Clock className="w-4 h-4" />, color: 'text-green-600 bg-green-50' },
              { label: 'Total Hours', value: result.totalHours.toLocaleString(), icon: <Moon className="w-4 h-4" />, color: 'text-blue-600 bg-blue-50' },
              { label: 'Total Minutes', value: result.totalMinutes.toLocaleString(), icon: <Star className="w-4 h-4" />, color: 'text-purple-600 bg-purple-50' },
            ].map((stat) => (
              <div key={stat.label} className={`p-3 rounded-lg border ${stat.color.split(' ')[1]} border-gray-100`}>
                <div className={`${stat.color.split(' ')[0]} mb-1`}>{stat.icon}</div>
                <p className="text-lg font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Birthday & fun facts */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="p-4 bg-pink-50 rounded-lg border border-pink-100">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-4 h-4 text-pink-600" />
                <span className="text-sm font-medium text-pink-800">Next Birthday</span>
              </div>
              <p className="text-2xl font-bold text-pink-600">{result.daysUntilBirthday} days</p>
              <p className="text-xs text-pink-500">{result.nextBirthday.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
            </div>
            <div className="p-4 bg-violet-50 rounded-lg border border-violet-100">
              <div className="flex items-center gap-2 mb-2">
                <Heart className="w-4 h-4 text-violet-600" />
                <span className="text-sm font-medium text-violet-800">Born on</span>
              </div>
              <p className="text-lg font-bold text-violet-600">{result.dayOfWeek}</p>
              <p className="text-xs text-violet-500">{result.zodiacEmoji} {result.zodiacSign} &bull; {result.birthstone} &bull; {result.season}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgeCalculator;
